import React from 'react'
import styled from '@emotion/styled'
import { GatsbyImage } from 'gatsby-plugin-image'

const StyledImage = styled(GatsbyImage)`
  width: 100%;
  ${ ({ ratio }) => ratio ? `
    > div:first-of-type {
      padding-top: ${ ratio * 100 }% !important;
    }
    img {
      display: block;
    }
  ` : '' }
	img {
    ${ ({ hotspot }) => hotspot ? `
    	object-position: ${ hotspot.x * 100 + '%' } ${ hotspot.y * 100 + '%' };
    ` : '' }
  }
`

// Bake the Sanity crop into the image data at render time (returns a new object,
// never mutates the source). Doing this during render — rather than in a
// post-hydration useEffect — means the crop is present in the server-rendered
// HTML, so the browser requests only the cropped image instead of fetching the
// uncropped srcSet first and the cropped one second (double the Sanity transforms).
const applyCropToImageData = (image, rect) => {
  const fallback = image?.images?.fallback
  if (!fallback) return image

  const rectReg = /rect=.*,\d{1,5}/g

  const src = fallback.src.match(rectReg)
    ? fallback.src.replaceAll(rectReg, ('&rect=' + rect))
    : fallback.src + '&rect=' + rect

  const srcSet = fallback.srcSet.match(rectReg)
    ? fallback.srcSet.replaceAll(rectReg, ('rect=' + rect))
    : fallback.srcSet.replaceAll('&auto=format', ('&auto=format&rect=' + rect))

  return {
    ...image,
    images: {
      ...image.images,
      fallback: { ...fallback, src, srcSet }
    }
  }
}

const Image = ({
  ratio,
  sizes,
  className,
  loading,
  alt,
  width,
  height,
  hotspot,
  crop,
  aspectRatio,
  image,
  ...rest
}) => {
  const croppedWidth = image?.width * (1 - crop?.left - crop?.right)
  const croppedHeight = image?.height * (1 - crop?.top - crop?.bottom)
  const croppedRatio = croppedHeight / croppedWidth

  // Only apply a rect when the editor actually cropped the image. An all-zero
  // crop is a full-frame rect (identical output), so skipping it lets the image
  // reuse its build-time URL — one fewer transform variant to generate and cache.
  const hasCrop = width && height && crop &&
    (crop.top || crop.bottom || crop.left || crop.right)

  let finalImage = image
  if (hasCrop) {
    const rectTop = Math.round(crop.top * height)
    const rectLeft = Math.round(crop.left * width)
    const rectBottom = crop.bottom * height
    const rectRight = crop.right * width
    const rectWidth = Math.round(width - rectLeft - rectRight)
    const rectHeight = Math.round(height - rectTop - rectBottom)
    const rect = rectLeft + ',' + rectTop + ',' + rectWidth + ',' + rectHeight
    finalImage = applyCropToImageData(image, rect)
  }

  return (
    <StyledImage
      {...rest}
      image={finalImage}
      ratio={ratio || croppedRatio}
      crop={crop}
      hotspot={hotspot}
      sizes={sizes}
      className={className}
      loading={loading}
      alt={alt}
      format={['auto', 'avif', 'webp']}
    />
  )
}

export default Image
